import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import * as Sentry from '@sentry/react'

import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch
} from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'

import { AsyncLoaderPage } from '../../components/async-loader/async-loader-page'
import { AsyncLoaderPopup } from '../../components/async-loader/async-loader-popup'
import Content from './components/app-content'
import { loadOwnProjects } from '../ProjectSelection/actions'
import {
  Loader,
  LoaderWrapper
} from '../../components/page-loader/styled-components'
import ProjectRouter from './components/app-project-router'
import PopupNew from '../../components/popup-new'

import NotFoundPage from '../NotFoundPage'
import {
  AppDiv,
  Page,
  Wrapper,
  PopupOverlay,
  MobilePopupOverlay
} from './styled-components'
import { type } from '../BottomBar/constants'
import { isActivePath, pageType } from '../../utils/isActivePath'
import { makeSelectProjectDetails } from '../ProjectSettings/selectors'
import { makeSelectPlan } from '../Plan/selectors'
import { makeSelectPin } from '../PinDetails/selectors'
import UpgradePlanPopup from '../UpgradePlanPopup'
import FeatureUnavailableDialog from '../FeatureAvailability/components/FeatureUnavailableDialog'
import {
  makeSelectGetUnavailableFeatureSection,
  makeSelectGetUnavailableFeatureSlug
} from '../FeatureAvailability/selectors'
import { painelFeatures } from '../FeatureAvailability/constants'
import OnboardingPopup from '../../components/onboarding-popup'
import FeaturebaseIntegration from '../../utils/feature-base'
import ErrorAlert from '../../components/error-boundary/components/error-boundary-alert'
import ErrorBoundaryHoc from '../../components/error-boundary/components/error-boundary-hoc'
import ForceLogin from '../authentication/components/force-login'
import ForceLogout from '../authentication/components/force-logout'
import { isEmpty } from 'lodash'
import {
  useAuthStore,
  useIsLoggedIn,
  useTokenAvailable
} from '../../zustand/authentication/use-auth-store'
import { useLoginGetUserMutation } from '../../react-query/authentication/mutations/use-login-logged-user-mutation'
import { useLoginRefreshMutation } from '../../react-query/authentication/mutations/use-login-refresh-mutation'
import {
  useBrowserStore,
  useIsDesktop,
  useIsMobile
} from '../../zustand/browser-store/use-browser-store'

import { useIntercom } from 'react-use-intercom'
import {
  getLicense,
  getPrivacyPolicyAccepted,
  getTermsAndConditionsAccepted,
  getUserId,
  useMeQuery
} from '../../react-query/authentication/queries/use-me-query'
import { setUser } from '../authentication/actions'
import { AppMtlsCertWrapper } from './components/app-mtls-cert-wrapper.jsx'
import { useMtlsCertificateStore } from '../../zustand/mtls-certificate/use-mtls-certificate-store.jsx'
import { SsoSelection } from '../authentication/components/sso-selection/index.jsx'
import { useGetJwtTokenQuery } from '../../react-query/featurebase/queries/use-get-jwt-token-query'
import { useUnleashContext } from '@unleash/proxy-client-react'
import SamlLogin from '../authentication/components/saml-login.jsx'

const NavigationSidebar = lazy(() => import('../navigation-sidebar'))
const LoginPopup = lazy(
  () => import('../authentication/components/login-popup')
)
const RegisterAccount = lazy(() => import('../register'))
const RegisterVerificationCode = lazy(
  () => import('../register-verification-code')
)

const UserSettings = lazy(() => import('../user-settings'))
const UserManagement = lazy(() => import('../UserManagement'))
const Reports = lazy(() => import('../Reports'))
const ImportQuickView = lazy(() => import('../ImportQuickView'))
const ProjectSelection = lazy(() => import('../ProjectSelection'))
const ActivateUser = lazy(() => import('../activate-user'))
const ImportStatus = lazy(() => import('../ImportStatus'))
const TopBar = lazy(() => import('../TopBar'))
const BottomBar = lazy(() => import('../BottomBar'))
const TermsAndConditionWarning = lazy(
  () => import('./components/app-terms-and-condition-warning')
)
const EnableTwoFa = lazy(() => import('./components/app-enable-twofa'))

const ONBOARDING_COMPLETE_KEY = 'onboardingComplete'

export const App = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const match = useRouteMatch()
  const intercom = useIntercom()
  const updateContext = useUnleashContext()

  const { formatMessage } = useIntl()

  const { mutate: handleGetUser } = useLoginGetUserMutation()

  const {
    mutate: handleRefreshToken,
    isPending: isLoadingRefreshToken,
    error: isErrorRefreshtoken
  } = useLoginRefreshMutation()

  const {
    initializedApp,
    expiredToken,
    hideMyProjects,
    showLoginPopup,
    setShowLoginPopup,
    passwordPolicy,
    setHideMyProjects,
    logout
  } = useAuthStore()

  const tokenAvailable = useTokenAvailable()
  const isLoggedIn = useIsLoggedIn()
  const userId = getUserId()
  const privacyPolicyAccepted = getPrivacyPolicyAccepted()
  const termsAndConditionsAccepted = getTermsAndConditionsAccepted()
  const license = getLicense()

  const isMobile = useIsMobile()
  const isDesktop = useIsDesktop()

  const {
    data: user,
    isLoading: isLoadingUser,
    refetch: getUser,
    error: isErrorUser
  } = useMeQuery({
    enabled: tokenAvailable
  })

  const { data: jwtToken } = useGetJwtTokenQuery({ enabled: !isEmpty(user) })

  const {
    bottomBar,
    menuOpen,
    setWindowSize,
    browserInfo,
    popupOverlay,
    mobilePopupOverlay,
    toggleMenu,
    closeMenu
  } = useBrowserStore()

  const [showOnboardingPopup, setShowOnboardingPopup] = useState(false)
  const { isMtlsRequired } = useMtlsCertificateStore()

  const isLoadingLogin =
    isLoadingRefreshToken || !initializedApp || isLoadingUser

  // Selectors
  const currentRoute = location.pathname
  const currentRouteHref = `${location.pathname}${location.search}`
  const projectDetails = useSelector(makeSelectProjectDetails())
  const plan = useSelector(makeSelectPlan())
  const pin = useSelector(makeSelectPin())
  const unavailableFeatureSlug = useSelector(
    makeSelectGetUnavailableFeatureSlug()
  )
  const unavailableFeatureSection = useSelector(
    makeSelectGetUnavailableFeatureSection()
  )

  // Effects
  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth, window.innerHeight)
    }

    window.addEventListener('resize', handleResize, { passive: true })

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (isErrorUser) {
      logout()
    }
  }, [isErrorUser])

  useEffect(() => {
    if (isErrorRefreshtoken) {
      setShowLoginPopup(true)
    }
  }, [isErrorRefreshtoken])

  useEffect(() => {
    // Intercom Logged-in user UPDATE
    if (!user || !intercom || !jwtToken) return

    const encodedJwt = encodeURIComponent(jwtToken.jwt)

    intercom.boot({
      customAttributes: {
        featurebase_jwt: encodedJwt
      }
    })

    const {
      firstName,
      lastName,
      phone,
      email,
      organisationName,
      organisationId,
      userIntercomHash,
      created,
      settings: { language },
      license: { type }
    } = user

    const intercomUserProps = {
      name: [firstName, lastName].filter(Boolean).join(' '),
      userId,
      userHash: userIntercomHash,
      email,
      languageOverride: language,
      phone,
      createdAt: created,
      company: {
        name: organisationName,
        companyId: organisationId,
        plan: type
      },
      customAttributes: {
        featurebase_jwt: encodedJwt
      }
    }

    intercom.update(intercomUserProps)

    if (user && updateContext) {
      const { organisationId, subscriptionInfo } = user

      updateContext({
        userId,
        properties: {
          organizationId: organisationId,
          subscriptionType: subscriptionInfo?.tier?.code ?? null
        }
      })
    }
  }, [user, jwtToken])

  useEffect(() => {
    if (tokenAvailable && !isEmpty(user)) {
      const hideProjects = !!localStorage.getItem(
        `hideMyProjects-${user.userId}`
      )

      if (hideProjects) setHideMyProjects()

      // dispatch to reset all reduxes ( should be removed after full refactor. )
      dispatch(setUser(user))
    }
  }, [user])

  useEffect(() => {
    if (tokenAvailable && isEmpty(user) && !isLoadingLogin) {
      getUser().then(({ data }) => {
        handleGetUser({ data })
      })
    }
  }, [tokenAvailable, user, isLoadingLogin])

  useEffect(() => {
    if (!isMtlsRequired && userId && !hideMyProjects) {
      showOnboarding()
      dispatch(loadOwnProjects())
    }
  }, [userId, hideMyProjects, isMtlsRequired])

  useEffect(() => {
    if (expiredToken) {
      handleRefreshToken()
    }
  }, [expiredToken])

  useEffect(() => {
    // Set Sentry scope when project, plan or pin changes
    if (typeof Sentry !== 'undefined' && Sentry?.configureScope) {
      Sentry.configureScope((scope) => {
        if (projectDetails) {
          scope.setTag('project_name', projectDetails?.name)
          scope.setTag('project_id', projectDetails?.id.replace(/-/g, ''))
        }
        if (plan) {
          scope.setTag('plan_id', plan?.id.replace(/-/g, ''))
          scope.setTag('plan_name', plan?.name)
        }
        if (pin) {
          scope.setTag('pin_id', pin?.id.replace(/-/g, ''))
          scope.setTag('pin_name', pin?.name)
        }
        if (user) {
          scope.setUser({ email: user.email })
        }
      })
    }
  }, [projectDetails, plan, pin, user])

  // Callbacks
  const showOnboarding = useCallback(() => {
    const onboardingComplete = Boolean(
      localStorage.getItem(`${ONBOARDING_COMPLETE_KEY}--${userId}`)
    )
    if (!onboardingComplete) {
      setShowOnboardingPopup(true)
    }
  }, [userId])

  const hideOnboardingPopup = useCallback(() => {
    localStorage.setItem(`${ONBOARDING_COMPLETE_KEY}--${userId}`, true)
    setShowOnboardingPopup(false)
  }, [userId])

  const handleRemoveState = useCallback(() => {
    history.push({ pathname: location.pathname })
    window.location.reload()
  }, [history, location.pathname])

  const handleToggleMenu = useCallback(() => {
    toggleMenu()
  }, [toggleMenu])

  const handleCloseMenu = useCallback(() => {
    closeMenu()
  }, [closeMenu])

  const topBarHeight = isLoggedIn && !isMobile ? 50 : 0
  let sideBarWidth = isLoggedIn ? 56 : 0
  if (isLoggedIn && isDesktop && menuOpen) {
    sideBarWidth = 250
  } else if (isLoggedIn && isMobile) {
    sideBarWidth = 0
  }

  const pPTaC = privacyPolicyAccepted && termsAndConditionsAccepted

  const bottomBarItems = [
    {
      type: type.PROJECTS,
      title: 'projects_projects',
      onClick: () => history.push('/'),
      active: isActivePath(pageType.PROJECTS, match)
    }
  ]

  const showEnableTwoFa =
    isLoggedIn &&
    passwordPolicy &&
    !passwordPolicy.sms &&
    user &&
    user.forceMfa &&
    user.type !== 'SAML'

  const protectedRoutes = useMemo(
    () => [
      // 1. Static Routes
      { path: '/project/move', component: ProjectSelection, exact: true },

      // 2. Dynamic and specific
      {
        path: '/project/settings/:projectId',
        component: ProjectSelection,
        exact: true
      },
      {
        path: '/project/new/:parentId?',
        component: ProjectSelection,
        exact: true
      },
      {
        path: '/project/newfolder/:parentId?',
        component: ProjectSelection,
        exact: true
      },
      {
        path: '/project/rename/:folderId?',
        component: ProjectSelection,
        exact: true
      },
      {
        path: '/project/:projectId/copy',
        component: ProjectSelection,
        exact: true
      },

      // 3. Project Routes
      {
        path: '/project/:projectId/*',
        component: ProjectRouter,
        exact: false
      },
      {
        path: '/project/:projectId',
        component: ProjectRouter,
        exact: true
      },

      // 4. General Routes
      {
        path: ['/home/:mode/:token?', '/home', '/project', '/'],
        component: ProjectSelection
      },

      // 5. Other routes
      { path: '/userSettings', component: UserSettings, exact: true },
      { path: '/userManagement', component: UserManagement, exact: false },
      { path: '/reports', component: Reports, exact: true }
    ],
    []
  )

  const unauthenticatedRoutes = useMemo(
    () => [
      { path: '/activate/:type/:token', component: ActivateUser, exact: true },
      {
        path: '/register/verification-code',
        component: RegisterVerificationCode,
        exact: true
      },
      {
        path: '/register/:invitedToken?',
        component: RegisterAccount,
        exact: true
      }
    ],
    []
  )

  const publicRoutes = useMemo(
    () => [
      { path: '/samlLogin', component: SamlLogin, exact: true },
      { path: '/ssoSelection', component: SsoSelection, exact: true },
      { path: '/idpSelection', component: SsoSelection, exact: true }
    ],
    []
  )

  return (
    <AppMtlsCertWrapper isLoggedIn={isLoggedIn}>
      <AppDiv overflowHidden={isLoggedIn}>
        <FeaturebaseIntegration user={user} />
        <ErrorAlert />

        {/* Componentes modais/globais */}
        {!pPTaC && isLoggedIn && (
          <Suspense fallback={AsyncLoaderPopup}>
            <TermsAndConditionWarning />
          </Suspense>
        )}

        {showEnableTwoFa && (
          <Suspense fallback={AsyncLoaderPopup}>
            <EnableTwoFa />
          </Suspense>
        )}

        {showLoginPopup && (
          <Suspense fallback={AsyncLoaderPopup}>
            <LoginPopup />
          </Suspense>
        )}

        {showOnboardingPopup && (
          <Suspense fallback={AsyncLoaderPopup}>
            <OnboardingPopup closePopup={hideOnboardingPopup} />
          </Suspense>
        )}

        {/* Loader principal */}
        {tokenAvailable && isLoadingLogin && (
          <LoaderWrapper>
            <Loader className="loader" />
          </LoaderWrapper>
        )}

        {/* Conteúdo principal quando não está carregando */}
        {!(tokenAvailable && isLoadingLogin) && (
          <Page visible>
            {isLoggedIn && !isMobile && (
              <Suspense fallback={null}>
                <TopBar
                  termsAndConditionsAccepted={pPTaC}
                  currentRoute={currentRoute}
                  currentRouteHref={currentRouteHref}
                  isMobile={isMobile}
                  height={topBarHeight}
                  loggedIn={isLoggedIn}
                  toggleMenu={handleToggleMenu}
                  menuOpen={menuOpen}
                  license={license}
                  history={history}
                  user={user}
                />
              </Suspense>
            )}

            <Wrapper topHeight={topBarHeight}>
              {/* Sidebar para rotas de projeto */}
              {isLoggedIn && (
                <Suspense fallback={null}>
                  <NavigationSidebar
                    showPopupOverlay={popupOverlay}
                    open={menuOpen}
                    loggedIn={isLoggedIn}
                    toggleMenu={handleToggleMenu}
                    closeMenu={handleCloseMenu}
                    currentRoute={currentRoute}
                    isMobile={isMobile}
                    isDesktop={isDesktop}
                  />
                </Suspense>
              )}

              <ErrorBoundaryHoc>
                <Content left={sideBarWidth}>
                  {browserInfo?.os !== 'iOS' && (
                    <PopupOverlay
                      visible={
                        (menuOpen && !isDesktop && isLoggedIn) || popupOverlay
                      }
                    />
                  )}

                  <MobilePopupOverlay visible={mobilePopupOverlay} />

                  <Suspense fallback={AsyncLoaderPage}>
                    <Switch>
                      {/* public routes */}
                      {publicRoutes.map(({ path, component: Component }) => (
                        <Route key={path} exact path={path}>
                          <Component />
                        </Route>
                      ))}

                      {/* Upgrade Routes */}
                      <Route path="/upgrade">
                        <Suspense fallback={AsyncLoaderPopup}>
                          <UpgradePlanPopup close={() => history.goBack()} />
                        </Suspense>
                      </Route>

                      {/* Import Routes */}
                      <Route path={['/importStatus', '*/importStatus']}>
                        <ForceLogin>
                          <ImportStatus />
                        </ForceLogin>
                      </Route>

                      {/* Protected Routes */}
                      {protectedRoutes.map(
                        ({ path, component: Component, exact = true }) => (
                          <Route key={path} path={path} exact={exact}>
                            <ForceLogin>
                              <Component />
                            </ForceLogin>
                          </Route>
                        )
                      )}

                      {/* Not Auth Routes */}
                      {unauthenticatedRoutes.map(
                        ({ path, component: Component }) => (
                          <Route key={path} exact path={path}>
                            <ForceLogout>
                              <Component />
                            </ForceLogout>
                          </Route>
                        )
                      )}

                      {/* 404 - Last route */}
                      <Route component={NotFoundPage} />
                    </Switch>
                  </Suspense>

                  {unavailableFeatureSlug &&
                    !painelFeatures.includes(unavailableFeatureSlug) && (
                      <FeatureUnavailableDialog
                        featureSlug={unavailableFeatureSlug}
                        featureSection={unavailableFeatureSection}
                      />
                    )}

                  {isLoggedIn && (
                    <Suspense fallback={AsyncLoaderPopup}>
                      <ImportQuickView sideBarWidth={sideBarWidth} />
                    </Suspense>
                  )}

                  {isLoggedIn && isMobile && bottomBar && (
                    <Suspense fallback={null}>
                      <BottomBar items={bottomBarItems} />
                    </Suspense>
                  )}
                </Content>
              </ErrorBoundaryHoc>
            </Wrapper>
          </Page>
        )}

        {history.location.state?.data === 'permission denied' && (
          <Suspense fallback={AsyncLoaderPopup}>
            <PopupNew
              title={formatMessage({ id: 'access_denied_heading' })}
              contentText={formatMessage({ id: 'access_denied_description' })}
              rightButtons={[
                {
                  onClick: handleRemoveState,
                  text: formatMessage({ id: 'ok' })
                }
              ]}
            />
          </Suspense>
        )}
      </AppDiv>
    </AppMtlsCertWrapper>
  )
}

export default App
